// Unit Export V1 — a polished, single-unit .xlsx report. Pure sheet-
// building logic: every input is data the CALLER already fetched through
// Compass's normal, permission-scoped hooks (useVisibleUnits/
// useVisibleUnitLeaders/useVisibleSyt — see src/hooks/useUnits.ts). This
// module never queries Firestore itself, so it structurally cannot see
// data the caller didn't already have — the same reason the live Unit
// Health drawer can't. canExportUnit()/canExportLeaderContactFields()
// (./unitExportPermissions) are still checked here too, as defense-in-
// depth, not as the only guard.
//
// Reuses the exact same computations the Unit Health drawer itself uses —
// computeUnitLeadershipSustainability() (Key 3 / Program Leadership /
// Training / SYT / Attention), evaluateHazardousWeather(), and the pure
// helpers in lib/unitHealth.ts (recharter status, advancement pace) —
// rather than re-deriving any of that logic a second time for the export.
//
// Deliberately CURRENT-STATE only. `snapshots` is always passed as `[]` to
// computeUnitLeadershipSustainability(), so `history.availability` is
// always 'none' and no historical delta/trend ever appears — Historical
// Data V2 (trend UI) is explicitly out of scope for this increment.
//
// SCOPE PRINCIPLE (V1): export reorganizes information the user can
// already access through the Unit Health EXPERIENCE (the drawer/tabs at
// /units) — never information that's merely Firestore-authorized but not
// actually surfaced there today. Commissioner connection/activity data was
// removed from this file for exactly that reason: `/commissioners` is a
// separate top-level page (see src/components/Layout.tsx's nav), not part
// of Unit Health, and grepping src/views/UnitHealth.tsx confirms it never
// renders `Unit.commissionerActivity`/`commissionerContactId` anywhere —
// so unlike everything else in this file, that data has no Unit-Health-
// side counterpart to "reorganize." If Compass later surfaces commissioner
// information IN Unit Health itself, this file's export can be extended
// to match at that point — see the completion report for this increment.

import {
  computeUnitLeadershipSustainability,
  type UnitLeadershipSustainability,
} from '../leadershipSustainability.ts';
import {
  advForUnit,
  advProgram,
  advancementState,
  evaluateHazardousWeather,
  hasRankLadder,
  HAZARDOUS_WEATHER_STATUS_LABELS,
  rechartBucket,
  rechartCountdown,
  type HazardousWeatherRoleInput,
} from '../unitHealth.ts';
import { canExportLeaderContactFields, canExportUnit } from './unitExportPermissions.ts';
import {
  blankRow,
  dateCell,
  dateTimeCell,
  fullWidthRow,
  labelCell,
  noteCell,
  numberCell,
  percentCell,
  sectionHeadingCell,
  subtitleCell,
  tableHeaderCell,
  titleCell,
  valueCell,
  type ReportRow,
  type ReportSheet,
} from './xlsxReport.ts';
import type { Unit, UnitLeader, UnitSyt, UserProfile } from '../../types.ts';

export interface UnitExportInput {
  unit: Unit;
  districtName: string;
  /** This unit's OWN unitLeaders docs only (e.g. leadersByUnit.get(unit.id)
   *  in UnitHealth.tsx) — already scoped by useVisibleUnitLeaders(profile)
   *  upstream. Never pass an unfiltered/council-wide leader list here. */
  leaders: UnitLeader[];
  /** Keyed by memberId — may be broader than this unit (e.g. the whole
   *  district's sytByMember map); only entries matching `leaders` are
   *  ever read. */
  sytByMember: Map<string, UnitSyt>;
  trainedLeadersImportExists: boolean;
  profile: UserProfile | null;
  generatedAt?: Date;
}

export type UnitExportResult =
  | { ok: true; sheets: ReportSheet[]; filename: string }
  | { ok: false; reason: string };

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function slug(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function labelValueRow(label: string, value: string | number | null | undefined, opts: { wrap?: boolean } = {}): ReportRow {
  return [labelCell(label), valueCell(value, opts)];
}

// ---- Unit Summary sheet ----------------------------------------------

const SUMMARY_COLS = 2;

export function buildUnitSummarySheet(input: UnitExportInput, result: UnitLeadershipSustainability, generatedAt: Date): ReportSheet {
  const { unit, districtName } = input;
  const rows: ReportRow[] = [];
  const full = (cell: Parameters<typeof fullWidthRow>[0]) => fullWidthRow(cell, SUMMARY_COLS);

  rows.push(full(titleCell('Compass — Unit Health Report')));
  rows.push(full({ ...valueCell(unit.name), fontWeight: 'bold', fontSize: 14 }));
  rows.push(full(subtitleCell(`${titleCase(unit.type)} · ${districtName}`)));
  rows.push([{ value: 'Generated', type: String, textColor: '#6c7f91' }, dateTimeCell(generatedAt)]);
  rows.push(blankRow());

  // Unit identity
  rows.push(full(sectionHeadingCell('Unit Identity')));
  rows.push(labelValueRow('Type', titleCase(unit.type)));
  rows.push(labelValueRow('District', districtName));
  if (unit.charterOrg) {
    rows.push(
      labelValueRow(
        'Chartered Organization',
        unit.charterOrgDetail?.orgType ? `${unit.charterOrg} — ${unit.charterOrgDetail.orgType}` : unit.charterOrg,
      ),
    );
  }
  if (unit.charterOrgDetail?.address || unit.charterOrgDetail?.cityState) {
    const addr = [unit.charterOrgDetail.address, unit.charterOrgDetail.cityState, unit.charterOrgDetail.zip]
      .filter(Boolean)
      .join(', ');
    rows.push(labelValueRow('Charter Org. Address', addr, { wrap: true }));
  }
  if (unit.charterOrgDetail?.phone) {
    rows.push(labelValueRow('Charter Org. Phone', unit.charterOrgDetail.phone));
  }
  rows.push(blankRow());

  // Charter / Recharter
  rows.push(full(sectionHeadingCell('Charter / Recharter')));
  if (unit.recharter) {
    const bucket = rechartBucket(unit);
    const countdown = rechartCountdown(unit);
    const statusLabel =
      bucket === 'overdue' ? 'Overdue' : bucket === 'soon' ? 'Due Soon' : bucket === 'chartered' ? 'Chartered' : 'Unknown';
    rows.push(labelValueRow('Status', statusLabel));
    rows.push([labelCell('Expires'), dateCell(unit.recharter.charterExpires)]);
    if (countdown) {
      rows.push(
        labelValueRow(
          'Days Remaining',
          countdown.days < 0 ? `Overdue by ${Math.abs(countdown.days)} day(s)` : `${countdown.days} day(s)`,
        ),
      );
    }
    rows.push([labelCell('Paid Youth'), numberCell(unit.recharter.paidYouth)]);
    rows.push([labelCell('Paid Adults'), numberCell(unit.recharter.paidAdults)]);
    rows.push([labelCell('Scout Life Youth'), numberCell(unit.recharter.scoutLifeYouth)]);
    rows.push([labelCell('Scout Life Adults'), numberCell(unit.recharter.scoutLifeAdults)]);
    rows.push([labelCell('As of'), dateCell(unit.recharter.reportDate)]);
  } else {
    rows.push(full(noteCell('No recharter report on file for this unit.')));
  }
  rows.push(blankRow());

  // Membership
  rows.push(full(sectionHeadingCell('Membership')));
  const yc = result.youthContext;
  if (yc.availability === 'available') {
    rows.push([labelCell('Youth'), numberCell(yc.youth)]);
    rows.push([labelCell('Registered Leaders'), numberCell(yc.leaders)]);
    rows.push([labelCell('Youth per Leader'), numberCell(yc.ratio)]);
    rows.push(labelValueRow('Source', yc.youthSource === 'recharter' ? 'Recharter' : yc.youthSource === 'advancement' ? 'Advancement' : '—'));
  } else {
    rows.push(full(noteCell('Insufficient data to compute membership figures for this unit.')));
  }
  rows.push(blankRow());

  // Key 3 / Leadership status
  rows.push(full(sectionHeadingCell('Key 3 / Leadership Status')));
  if (result.keyLeadership.availability === 'available') {
    for (const seat of result.keyLeadership.seats) {
      rows.push(labelValueRow(seat.seatLabel, seat.observed ? `Filled — ${seat.personName ?? 'name not on file'}` : 'Not filled'));
    }
    const missing = result.keyLeadership.seats.filter((s) => !s.observed).length;
    rows.push(labelValueRow('Overall', missing === 0 ? 'Complete' : `Missing ${missing} seat${missing === 1 ? '' : 's'}`));
    rows.push([labelCell('As of'), dateCell(result.keyLeadership.asOf)]);
  } else {
    rows.push(full(noteCell('No Key 3 report on file for this unit.')));
  }
  rows.push(blankRow());

  // Training
  rows.push(full(sectionHeadingCell('Training')));
  if (result.readiness.availability === 'available') {
    rows.push(
      labelValueRow('Trained', `${result.readiness.trainedCount} of ${result.readiness.totalLeaders}`),
    );
    rows.push([labelCell('Trained %'), percentCell(result.readiness.trainedPercent ?? undefined)]);
  } else {
    rows.push(full(noteCell('No Trained Leaders report on file for this unit.')));
  }
  if (result.programFacing.availability === 'available') {
    rows.push(
      labelValueRow(
        'Program-Facing Leaders Trained',
        `${result.programFacing.trainedCount} of ${result.programFacing.totalCount}`,
      ),
    );
  }
  if (result.programLeadership.availability === 'available') {
    rows.push(labelValueRow('Program Leadership', result.programLeadership.summary, { wrap: true }));
  }
  rows.push(blankRow());

  // SYT
  rows.push(full(sectionHeadingCell('Safeguarding Youth Training (SYT)')));
  if (result.readiness.availability === 'available') {
    rows.push([labelCell('Current'), numberCell(result.readiness.sytCurrentCount)]);
    rows.push([labelCell('Not Current'), numberCell(result.readiness.sytNotCurrentCount)]);
    rows.push([labelCell('No Record'), numberCell(result.readiness.sytNoRecordCount)]);
    const usable = result.readiness.sytCurrentCount + result.readiness.sytNotCurrentCount;
    const pct = usable > 0 ? Math.round((result.readiness.sytCurrentCount / usable) * 1000) / 10 : null;
    rows.push([labelCell('% Current (of usable)'), percentCell(pct ?? undefined)]);
  } else {
    rows.push(full(noteCell('No SYT data available for this unit.')));
  }
  rows.push(blankRow());

  // Advancement
  rows.push(full(sectionHeadingCell('Advancement')));
  if (!hasRankLadder(unit)) {
    rows.push(full(noteCell(`${titleCase(unit.type)}s do not have a rank ladder in Compass.`)));
  } else {
    const snap = advForUnit(unit);
    if (snap) {
      const state = advancementState(unit, snap);
      rows.push(labelValueRow('Program', advProgram(unit) === 'cub' ? 'Cub Scouting' : 'Scouts BSA'));
      rows.push(labelValueRow('Program Year', snap.programYear));
      if (state) rows.push(labelValueRow('Pace', state.label));
      rows.push([labelCell('Ranks YTD'), numberCell(snap.ranksYtd)]);
      if (unit.type === 'troop') {
        rows.push([labelCell('Eagles YTD'), numberCell(snap.eaglesYtd)]);
        rows.push([labelCell('Merit Badges YTD'), numberCell(snap.meritBadgesYtd)]);
      }
      rows.push([labelCell('Youth (advancement report)'), numberCell(snap.youth)]);
      rows.push([labelCell('As of'), dateCell(snap.reportDate)]);
    } else {
      rows.push(full(noteCell('No advancement report on file for this unit.')));
    }
  }
  rows.push(blankRow());

  // Attention (factual signals only — see the in-app "How Attention Works"
  // explainer this mirrors; never a health score).
  rows.push(full(sectionHeadingCell('Attention')));
  rows.push(full(noteCell('Factual signals only — not a determination that this unit is healthy, weak, or at risk.')));
  if (result.attentionSignals.length === 0) {
    rows.push(full(noteCell('No attention items currently flagged for this unit.')));
  } else {
    for (const s of result.attentionSignals) {
      rows.push(labelValueRow(s.label, s.detail, { wrap: true }));
    }
  }

  return {
    name: 'Unit Summary',
    rows,
    columns: [{ width: 30 }, { width: 55 }],
  };
}

// ---- Leadership sheet --------------------------------------------------

interface MergedUnitLeader {
  memberId: string;
  fullName: string;
  positions: string[];
  untrainedPositions: string[];
  trained: boolean;
  topFirst: boolean; // any directContact position — sorts leadership-facing people first
  /** Every current position this person holds, in the exact shape
   *  evaluateHazardousWeather() (lib/unitHealth.ts) needs — the SAME
   *  canonical evaluator the Leaders table/drawer already use, not a
   *  re-derived one. */
  hazardousWeatherRoles: HazardousWeatherRoleInput[];
}

export function mergeUnitLeaders(leaders: UnitLeader[]): MergedUnitLeader[] {
  const byMember = new Map<string, MergedUnitLeader>();
  for (const l of leaders) {
    const where = l.unitName ?? 'this unit';
    const positions = l.positions.map((p) => p.position);
    const untrained = l.positions.filter((p) => !p.trained).map((p) => p.position);
    const topFirst = l.positions.some((p) => p.directContact);
    const roles: HazardousWeatherRoleInput[] = l.positions.map((p) => ({
      position: p.position,
      where,
      directContact: p.directContact,
      incompleteCourses: p.incompleteCourses,
    }));
    const cur = byMember.get(l.memberId);
    if (!cur) {
      byMember.set(l.memberId, {
        memberId: l.memberId,
        fullName: l.fullName,
        positions: [...positions],
        untrainedPositions: [...untrained],
        trained: !l.anyUntrained,
        topFirst,
        hazardousWeatherRoles: roles,
      });
      continue;
    }
    for (const p of positions) if (!cur.positions.includes(p)) cur.positions.push(p);
    for (const p of untrained) if (!cur.untrainedPositions.includes(p)) cur.untrainedPositions.push(p);
    cur.trained = cur.trained && !l.anyUntrained;
    cur.topFirst = cur.topFirst || topFirst;
    cur.hazardousWeatherRoles.push(...roles);
  }
  return [...byMember.values()].sort(
    (a, b) => Number(b.topFirst) - Number(a.topFirst) || a.fullName.localeCompare(b.fullName),
  );
}

function sytStatusLabel(syt: UnitSyt | undefined): string {
  if (!syt) return 'No Record';
  return syt.isCurrent ? 'Current' : 'Not Current';
}

export function buildLeadershipSheet(input: UnitExportInput, includeContactFields: boolean): ReportSheet {
  const merged = mergeUnitLeaders(input.leaders);
  const headers = ['Name', 'Position(s)', 'Trained', 'Untrained Positions', 'SYT Status', 'SYT Expires', 'Hazardous Weather'];
  if (includeContactFields) headers.push('Email', 'Phone');
  const cols = headers.length;

  const rows: ReportRow[] = [];
  rows.push(fullWidthRow({ ...titleCell(`Leadership — ${input.unit.name}`), fontSize: 13 }, cols));
  rows.push(headers.map(tableHeaderCell));

  if (merged.length === 0) {
    rows.push(fullWidthRow(noteCell('No registered leaders on file for this unit.'), cols));
  } else {
    for (const m of merged) {
      const syt = input.sytByMember.get(m.memberId);
      const leaderDoc = input.leaders.find((l) => l.memberId === m.memberId);
      // Applicability, and completion/expiration dates when Compass
      // actually has them, come from this SAME evaluator the live Leaders
      // table uses — see evaluateHazardousWeather()'s own header comment
      // in lib/unitHealth.ts for why completedDate/expirationDate are
      // null for every real leader today (no import currently supplies a
      // completion date): this column deliberately shows status only,
      // never a fabricated or always-empty date column.
      const hw = evaluateHazardousWeather(m.hazardousWeatherRoles);
      const row: ReportRow = [
        valueCell(m.fullName),
        valueCell(m.positions.join(', '), { wrap: true }),
        valueCell(m.trained ? 'Yes' : 'No'),
        valueCell(m.untrainedPositions.join(', ') || '—', { wrap: true }),
        valueCell(sytStatusLabel(syt)),
        syt?.expiresOn ? dateCell(syt.expiresOn) : valueCell(''),
        valueCell(HAZARDOUS_WEATHER_STATUS_LABELS[hw.status]),
      ];
      if (includeContactFields) {
        row.push(valueCell(leaderDoc?.email || ''));
        row.push(valueCell(syt?.phone || ''));
      }
      rows.push(row);
    }
  }

  const columns = [{ width: 26 }, { width: 32 }, { width: 10 }, { width: 30 }, { width: 13 }, { width: 13 }, { width: 16 }];
  if (includeContactFields) columns.push({ width: 26 }, { width: 16 });

  return {
    name: 'Leadership',
    rows,
    columns,
    stickyRowsCount: 2,
  };
}

// ---- Advancement sheet ---------------------------------------------------

const ADVANCEMENT_COLS = 2;

function rankLabel(key: string): string {
  const overrides: Record<string, string> = { light: 'AOL', secondclass: 'Second Class', firstclass: 'First Class' };
  return overrides[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
}

export function buildAdvancementSheet(input: UnitExportInput): ReportSheet {
  const { unit } = input;
  const rows: ReportRow[] = [];
  const full = (cell: Parameters<typeof fullWidthRow>[0]) => fullWidthRow(cell, ADVANCEMENT_COLS);
  rows.push(full({ ...titleCell(`Advancement — ${unit.name}`), fontSize: 13 }));
  rows.push(blankRow());

  if (!hasRankLadder(unit)) {
    rows.push(full(noteCell(`${titleCase(unit.type)}s do not have a rank ladder in Compass — no advancement pace is tracked for this unit type.`)));
    return { name: 'Advancement', rows, columns: [{ width: 40 }, { width: 40 }] };
  }

  const snap = advForUnit(unit);
  if (!snap) {
    rows.push(full(noteCell('No advancement report on file for this unit.')));
    return { name: 'Advancement', rows, columns: [{ width: 40 }, { width: 40 }] };
  }

  const state = advancementState(unit, snap);
  rows.push(labelValueRow('Program', advProgram(unit) === 'cub' ? 'Cub Scouting' : 'Scouts BSA'));
  rows.push(labelValueRow('Program Year', snap.programYear));
  rows.push([labelCell('As of'), dateCell(snap.reportDate)]);
  rows.push([labelCell('Youth'), numberCell(snap.youth)]);
  rows.push([labelCell('Ranks YTD'), numberCell(snap.ranksYtd)]);
  if (unit.type === 'troop') {
    rows.push([labelCell('Eagles YTD'), numberCell(snap.eaglesYtd)]);
    rows.push([labelCell('Merit Badges YTD'), numberCell(snap.meritBadgesYtd)]);
  }
  if (state) {
    rows.push(labelValueRow('Pace', state.label));
  }
  rows.push(blankRow());

  const byRankEntries = Object.entries(snap.byRank).filter(([, count]) => count > 0);
  if (byRankEntries.length > 0) {
    rows.push(full(sectionHeadingCell('Ranks Earned YTD by Rank')));
    rows.push([tableHeaderCell('Rank'), tableHeaderCell('Count YTD')]);
    for (const [key, count] of byRankEntries.sort((a, b) => rankLabel(a[0]).localeCompare(rankLabel(b[0])))) {
      rows.push([valueCell(rankLabel(key)), numberCell(count)]);
    }
  }

  return { name: 'Advancement', rows, columns: [{ width: 26 }, { width: 20 }] };
}

// ---- Top-level entry point -----------------------------------------------

export function buildUnitExport(input: UnitExportInput): UnitExportResult {
  if (!canExportUnit(input.profile, input.unit)) {
    return { ok: false, reason: 'You do not have permission to export this unit.' };
  }
  const includeContactFields = canExportLeaderContactFields(input.profile, input.unit);
  const generatedAt = input.generatedAt ?? new Date();

  const result = computeUnitLeadershipSustainability({
    unit: input.unit,
    unitLeaders: input.leaders,
    sytByMember: input.sytByMember,
    trainedLeadersImportExists: input.trainedLeadersImportExists,
    snapshots: [], // current-state only — no historical trend in V1
  });

  const sheets: ReportSheet[] = [
    buildUnitSummarySheet(input, result, generatedAt),
    buildLeadershipSheet(input, includeContactFields),
    buildAdvancementSheet(input),
  ];

  const filename = `unit-export-${slug(input.unit.name)}-${generatedAt.toISOString().slice(0, 10)}.xlsx`;
  return { ok: true, sheets, filename };
}
